import React from "react"
import { JID } from "@xmpp/jid"
import { Client, client } from "@xmpp/client"
import debug from "@xmpp/debug"
import XmppEventHandlers from "./xmppEventHandlers"
import type { AppStore } from "../reducers/store"
import XmppAnalytics from "./xmppAnalytics"
import * as Sentry from "@sentry/react"
import { captureError } from "../ErrorHandlers"
import SendoutQueueWorker from "../SendoutQueueWorker"
import MUC from "../xmpp_plugins/MUC"
import { markMetricDuration } from "../loadingMetrics"
import { keepConnected } from "./keepConnected"
import { initPubSubSync } from "./pubSubSync"
import { setupPerformInitialLoadOnOnline } from "../routes/archiveLoader"

let initializedXmppClientJid: JID | null = null
// The connect that is still running. A second sign-in (a double click on the
// button) must join it instead of starting another client: the app works
// through window.XMPP, and when two clients race the one left on window can
// be the loser, stopped with no socket, while keepConnected watches the
// winner and never notices.
let pendingConnection: Promise<ConnectionAttemptResult> | null = null

export const setupXmppListeners = (XMPP: Client, store: AppStore) => {
  XMPP.on("disconnect", XmppEventHandlers.handleOnDisconnect(store.dispatch))
  XMPP.on("offline", XmppEventHandlers.handleOnOffline(store.dispatch))
  XMPP.on("stanza", XmppEventHandlers.handleOnStanza(store, XMPP))
  XMPP.on("online", (jid) => XmppEventHandlers.handleOnOnline(XMPP, store, jid))
  XMPP.on("status", XmppEventHandlers.handleConnectionStatusChange(XMPP))
  XMPP.reconnect.on("reconnecting", () =>
    XmppEventHandlers.handleReconnectStatusChange("reconnecting"),
  )
  XMPP.reconnect.on("reconnected", () =>
    XmppEventHandlers.handleReconnectStatusChange("reconnected"),
  )
  setupPerformInitialLoadOnOnline(XMPP, store)
  initPubSubSync(XMPP)
  window.sendoutQueueWorker = SendoutQueueWorker(store, XMPP)
  window.mucPlugin = MUC(XMPP, (roomJid: string) =>
    XmppEventHandlers.handleRoomConfigChange(
      XMPP,
      roomJid,
      store.dispatch,
      store.getState,
    ),
  )

  const { iqCallee } = XMPP

  iqCallee.set(
    "http://jabber.org/protocol/commands",
    "command",
    XmppEventHandlers.handleNewClientVersionCommand(store.dispatch),
  )

  if (import.meta.env.MODE !== "test") {
    XMPP.on("send", XmppAnalytics.trackOnSend(XMPP))
    XMPP.on(
      "status",
      XmppAnalytics.trackOnConnectionStatus.bind(undefined, XMPP),
    )
    XMPP.on("error", XmppAnalytics.trackOnError.bind(undefined, XMPP))
    XMPP.reconnect.on(
      "reconnecting",
      XmppAnalytics.trackOnReconnecting.bind(undefined, XMPP),
    )
    XMPP.reconnect.on(
      "reconnected",
      XmppAnalytics.trackOnReconnected.bind(undefined, XMPP),
    )
  }
}

export const setXmppOnWindow = (userAuth: Types.Api.UserAuth) => {
  const [userName, domain] = userAuth.email.split("@")

  if (!userName || !domain) {
    return false
  }

  const xmpp = client({
    service: import.meta.env.REACT_APP_XMPP_SERVICE,
    domain: domain,
    username: userName,
    password: userAuth.password,
    resource: ["demo", "beedee.eu"].includes(domain)
      ? userAuth.resource
      : undefined,
  })
  window.XMPP = xmpp
  window.providedEmail = userAuth.email

  return true
}

export type ConnectionAttemptResult =
  | {
      connected: true
      jid: JID
    }
  | {
      connected: false
      error: "not-authorized" | "other" | null
    }

export const connect = async (
  store: AppStore,
): Promise<ConnectionAttemptResult> => {
  if (pendingConnection) {
    window.analytics.track("MultipleConnectAttempt", { pending: true })
    return pendingConnection
  }

  pendingConnection = connectWindowXmpp(store)
  try {
    return await pendingConnection
  } finally {
    pendingConnection = null
  }
}

const connectWindowXmpp = async (
  store: AppStore,
): Promise<ConnectionAttemptResult> => {
  return Sentry.startSpanManual(
    { name: "connect", op: "xmpp" },
    async (span) => {
      const start = window.performance.now()
      const xmpp = window.XMPP

      if (!xmpp) {
        return { connected: false, error: "other" }
      }

      if (initializedXmppClientJid) {
        window.analytics.track("MultipleConnectAttempt")
        return { connected: true, jid: initializedXmppClientJid }
      }

      setupXmppListeners(xmpp, store)

      // debug(xmpp, import.meta.env.REACT_APP_XMPP_DEBUG === "1")

      try {
        const jid = await xmpp.start()
        keepConnected(xmpp)
        initializedXmppClientJid = jid
        return { connected: true, jid }
      } catch (error: any) {
        if (
          error.message === "host-unknown" ||
          error.message.includes("not-authorized")
        ) {
          return { connected: false, error: "not-authorized" }
        }
        captureError(error, { origin: "InitialLoad" })
        await xmpp.stop()
        return { connected: false, error: "other" }
      } finally {
        if (span) span.end()
        markMetricDuration("xmppConnect", window.performance.now() - start)
      }
    },
  )
}

export const __resetXmppClientForTests = () => {
  initializedXmppClientJid = null
  pendingConnection = null
}

export const resetCredentialsAndConnect = async (
  userAuth: Types.Api.UserAuth,
  store: AppStore,
): Promise<ConnectionAttemptResult> => {
  // Swapping window.XMPP while a client is connecting or online would leave
  // the app on a client that never starts.
  if (!pendingConnection && !initializedXmppClientJid) {
    setXmppOnWindow(userAuth)
  }
  return await connect(store)
}

export type XmppContextType = {
  client: Client
  myJid: JID
}

export const xmppServiceAddress = (
  serviceName: string,
  client: Client,
): string => {
  if (!client.jid)
    throw `Can not build service address for ${serviceName}, jid on xmpp client is missing`

  return `${serviceName}.${client.jid.domain}`
}

export type AnonymousConnectionCallbacks = {
  onOnline?: (jid: JID) => void
  onOffline?: () => void
  onError?: (error: Error) => void
}

/**
 * Sets up an anonymous XMPP connection for unauthenticated users.
 * Used by public pages like AnalyzeOrderOnPage.
 *
 * @param anonymousId - A unique identifier to use as the XMPP resource (for traceability with analytics)
 * @param callbacks - Optional callbacks for connection events
 * @returns The XMPP client instance, or undefined if connection fails
 */
export const setupAnonymousXmppConnection = async (
  anonymousId: string,
  store: AppStore,
  callbacks?: AnonymousConnectionCallbacks,
): Promise<Client | undefined> => {
  // Skip if already connected
  if (window.XMPP) {
    if (callbacks?.onOnline && window.XMPP.jid) {
      callbacks.onOnline(window.XMPP.jid)
    }
    return window.XMPP
  }

  try {
    const xmpp = client({
      service: import.meta.env.REACT_APP_XMPP_SERVICE,
      domain: "anonymous",
      resource: anonymousId,
    })

    window.XMPP = xmpp
    window.anonymousXMPP = true

    setupXmppListeners(xmpp, store)

    xmpp.on("error", (err) => {
      console.error("XMPP error:", err)
      callbacks?.onError?.(err)
    })

    xmpp.on("online", (jid) => {
      console.log("XMPP connected anonymously as:", jid.toString())
      callbacks?.onOnline?.(jid)
      window.analytics?.track("Anonymous_XmppConnected", {
        jid: jid.toString(),
        anonymousId,
      })
    })

    xmpp.on("offline", () => {
      console.log("XMPP offline")
      callbacks?.onOffline?.()
    })

    await xmpp.start()
    return xmpp
  } catch (err) {
    console.error("Failed to connect to XMPP anonymously:", err)
    window.analytics?.track("Anonymous_XmppConnectionFailed", {
      error: err instanceof Error ? err.message : String(err),
      anonymousId,
    })
    return undefined
  }
}

/**
 * Stops the anonymous XMPP connection if it exists.
 * Safe to call even if no connection exists.
 */
export const stopAnonymousXmppConnection = async (): Promise<void> => {
  if (window.XMPP && window.XMPP.options?.domain === "anonymous") {
    await window.XMPP.stop().catch(console.error)
    window.XMPP = undefined
    window.anonymousXMPP = undefined
  }
}

export const XmppContext = React.createContext({} as XmppContextType)
